import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import API_URL from "../apiConfig";

export default function AccountStatementReport({ token }) {
  const [accountHead, setAccountHead] = useState("");
  const [type, setType] = useState("income");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState([]);
  const [accountHeadOptions, setAccountHeadOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchHeads = async () => {
      try {
        let url = `${API_URL}/api/income_expense/account_heads?type=${type}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch account heads");
        const heads = await res.json();
        setAccountHeadOptions(heads);
      } catch {
        setAccountHeadOptions([]);
      }
    };
    fetchHeads();
  }, [type, token]);

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    let url = `${API_URL}/api/income_expense/entries?type=${type}`;
    if (accountHead) url += `&account_head=${encodeURIComponent(accountHead)}`;
    if (from) url += `&from=${from}`;
    if (to) url += `&to=${to}`;
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch report");
      const entries = await res.json();
      setData(entries);
    } catch (err) {
      setError(err.message);
      setData([]);
    }
    setLoading(false);
  };

  const formatDateOnly = (dt) => {
    if (!dt) return "";
    if (/^\d{2}-\d{2}-\d{4}$/.test(dt)) return dt;
    const d = new Date(dt);
    if (isNaN(d.getTime())) return dt;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "A4",
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text("AADHAR FOUNDATION'S", 420, 50, { align: "center" });
    doc.setFontSize(14);
    doc.text("SAROJINI COLLEGE OF NURSING, DHULE", 420, 70, { align: "center" });
    doc.setFontSize(13);
    if (type === "income") {
      doc.setTextColor(0, 128, 0);
      doc.text("INCOME ACCOUNT STATEMENT REPORT", 420, 90, { align: "center" });
    } else {
      doc.setTextColor(255, 0, 0);
      doc.text("EXPENSE ACCOUNT STATEMENT REPORT", 420, 90, { align: "center" });
    }
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);

    let y = 110;
    if (from || to) {
      const dateText = `AS ON ${from ? formatDateOnly(from) : ""} TO ${to ? formatDateOnly(to) : ""}`;
      doc.text(dateText, 420, y, { align: "center" });
      y += 15;
    }
    if (accountHead) {
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 180);
      doc.text(`Account Head: ${accountHead}`, 420, y, { align: "center" });
      doc.setTextColor(0, 0, 0);
      y += 15;
    }

    const tableBody = data.map((row, idx) => [
      idx + 1,
      formatDateOnly(row.date),
      row.account_head || row.account,
      row.particulars,
      row.reference,
      Number(row.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      row.mode,
      row.student_name,
      row.roll_no,
      row.batch,
      row.class,
      row.session,
    ]);

    const totalAmount = data.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    tableBody.push([
      { content: "Total", colSpan: 5, styles: { fontStyle: "bold", halign: "right" } },
      { content: totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 }), styles: { fontStyle: "bold" } },
      "", "", "", "", ""
    ]);

    autoTable(doc, {
      head: [[
        "Sr No",
        "Date",
        "Account Head",
        "Particulars",
        "Reference",
        "Amount",
        "Mode",
        "Student Name",
        "Roll",
        "Batch",
        "Class",
        "Session"
      ]],
      body: tableBody,
      startY: y + 5,
      styles: {
        font: "helvetica",
        fontSize: 10,
        cellPadding: { top: 4, right: 6, bottom: 4, left: 6 },
        lineColor: [200, 200, 200],
        lineWidth: 0.5,
      },
      headStyles: {
        fillColor: [34, 68, 136],
        textColor: [255, 255, 255],
        fontStyle: "bold"
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      },
      columnStyles: {
        0: { cellWidth: 34 },
        1: { cellWidth: 62 },
        2: { cellWidth: 78 },
        3: { cellWidth: 82 },
        4: { cellWidth: 62 },
        5: { cellWidth: 64, halign: "right" },
        6: { cellWidth: 48 },
        7: { cellWidth: 92 },
        8: { cellWidth: 38 },
        9: { cellWidth: 48 },
        10: { cellWidth: 50 },
        11: { cellWidth: 55 },
      },
      theme: "grid"
    });

    doc.save("Account_Statement_Report.pdf");
  };

  const handleDownloadExcel = () => {
    const wsData = [
      [
        "Sr No",
        "Date",
        type === "income" ? "Income Account Head" : "Expense Account Head",
        "Particulars",
        "Reference",
        "Amount",
        "Mode",
        "Student Name",
        "Roll",
        "Batch",
        "Class",
        "Session"
      ],
      ...data.map((row, idx) => [
        idx + 1,
        formatDateOnly(row.date),
        row.account_head || row.account,
        row.particulars,
        row.reference,
        Number(row.amount),
        row.mode,
        row.student_name,
        row.roll_no,
        row.batch,
        row.class,
        row.session,
      ])
    ];
    wsData.push([
      "Total", "", "", "", "",
      data.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
      "", "", "", "", "", ""
    ]);
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Account Statement Report");
    XLSX.writeFile(wb, "Account_Statement_Report.xlsx");
  };

  return (
    <div className="reports-section">
      <h3>Account Statement Report</h3>
      <div style={{ marginBottom: 12 }}>
        <select value={type} onChange={e => setType(e.target.value)}>
          <option value="income">Income</option>
          <option value="expense">Expense</option>
        </select>
        <select
          value={accountHead}
          onChange={e => setAccountHead(e.target.value)}
          style={{ marginLeft: 8 }}
        >
          <option value="">All Account Heads</option>
          {accountHeadOptions.map((head, idx) => (
            <option key={idx} value={head}>
              {head}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={from}
          onChange={e => setFrom(e.target.value)}
          style={{ marginLeft: 8 }}
        />
        <input
          type="date"
          value={to}
          onChange={e => setTo(e.target.value)}
          style={{ marginLeft: 8 }}
        />
        <button style={{ marginLeft: 8 }} onClick={fetchReport}>
          Get Statement
        </button>
        {data && data.length > 0 && (
          <>
            <button
              style={{
                marginLeft: 8,
                background: "#224488",
                color: "#fff",
                border: "none",
                padding: "7px 18px",
                borderRadius: 5,
                cursor: "pointer",
                fontWeight: 600
              }}
              onClick={handleDownloadPDF}
            >
              Download PDF
            </button>
            <button
              style={{
                marginLeft: 8,
                background: "#007a3d",
                color: "#fff",
                border: "none",
                padding: "7px 18px",
                borderRadius: 5,
                cursor: "pointer",
                fontWeight: 600
              }}
              onClick={handleDownloadExcel}
            >
              Download Excel
            </button>
          </>
        )}
      </div>
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: "red" }}>{error}</div>}
      {data && data.length > 0 && (
        <table className="iem-table">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Date</th>
              <th>{type === "income" ? "Income Account Head" : "Expense Account Head"}</th>
              <th>Particulars</th>
              <th>Reference</th>
              <th>Amount</th>
              <th>Mode</th>
              <th>Student Name</th>
              <th>Roll</th>
              <th>Batch</th>
              <th>Class</th>
              <th>Session</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>{formatDateOnly(row.date)}</td>
                <td>{row.account_head || row.account}</td>
                <td>{row.particulars}</td>
                <td>{row.reference}</td>
                <td>{Number(row.amount).toLocaleString("en-IN", {minimumFractionDigits:2})}</td>
                <td>{row.mode}</td>
                <td>{row.student_name}</td>
                <td>{row.roll_no}</td>
                <td>{row.batch}</td>
                <td>{row.class}</td>
                <td>{row.session}</td>
              </tr>
            ))}
            <tr>
              <th colSpan={5}>Total</th>
              <th>
                {data.reduce((sum, row) => sum + (Number(row.amount) || 0), 0).toLocaleString("en-IN", {minimumFractionDigits:2})}
              </th>
              <th colSpan={6}></th>
            </tr>
          </tbody>
        </table>
      )}
      {data && !loading && data.length === 0 && <div>No data found.</div>}
    </div>
  );
}